import {
  apiGetPulseAuditLog,
  apiGetPulseAuditTimeline,
} from '@/api/marketingModule';

/**
 * The Market Pulse compliance audit log, as read by the Marketing Module.
 *
 * Read-only by design: the backend tables are append-only, so there is no action
 * here that writes, amends or deletes a record. Paging is server-side — the log
 * grows without bound, so the table asks for one window at a time and the total
 * comes back with it.
 */

export const DEFAULT_PAGE_SIZE = 50;

export const EMPTY_FILTERS = {
  event_id: '',
  source: '',
  action: '',
  advisor_account_id: '',
  date_from: '',
  date_to: '',
};

// Guards against a slow earlier response overwriting a newer one: the filter row
// can fire several requests in quick succession, and the last one asked for is
// the one the user is looking at.
let latestFetchToken = 0;

export default {
  namespaced: true,

  state: () => ({
    rows: [],
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    // Everyone who appears anywhere in the log, so the advisor filter can offer
    // names instead of account ids. Sent with every page, not just the first.
    advisors: [],
    filters: { ...EMPTY_FILTERS },
    // The event whose full trail is open in the detail dialog, or null.
    timeline: null,
    timelineLoading: false,
    loading: false,
    error: '',
  }),

  getters: {
    auditRows: (state) => state.rows,
    auditTotal: (state) => state.total,
    hasActiveFilters: (state) => Object.values(state.filters).some((value) => value !== ''),
  },

  mutations: {
    setLoading(state, loading) {
      state.loading = loading;
    },
    setError(state, error) {
      state.error = error;
    },
    setLog(state, {
      rows, total, page, pageSize, advisors,
    }) {
      state.rows = rows;
      state.total = total;
      state.page = page;
      state.pageSize = pageSize;
      state.advisors = advisors;
    },
    setFilters(state, filters) {
      state.filters = { ...state.filters, ...filters };
      // Any filter change invalidates the current window.
      state.page = 1;
    },
    resetFilters(state) {
      state.filters = { ...EMPTY_FILTERS };
      state.page = 1;
    },
    setPage(state, page) {
      state.page = page;
    },
    setPageSize(state, pageSize) {
      state.pageSize = pageSize;
    },
    setTimelineLoading(state, loading) {
      state.timelineLoading = loading;
    },
    setTimeline(state, timeline) {
      state.timeline = timeline;
    },
  },

  actions: {
    async fetchLog({ state, commit }, { page, pageSize } = {}) {
      latestFetchToken += 1;
      const token = latestFetchToken;
      commit('setLoading', true);
      commit('setError', '');
      try {
        const result = await apiGetPulseAuditLog({
          page: page || state.page,
          pageSize: pageSize || state.pageSize,
          filters: state.filters,
        });
        if (token !== latestFetchToken) return;
        commit('setLog', {
          rows: result.rows || [],
          total: result.total || 0,
          page: result.page,
          pageSize: result.page_size,
          advisors: result.advisors || [],
        });
      } catch (e) {
        if (token !== latestFetchToken) return;
        commit('setError', 'Could not load the pulse audit log.');
      } finally {
        if (token === latestFetchToken) commit('setLoading', false);
      }
    },

    async applyFilters({ commit, dispatch }, filters) {
      commit('setFilters', filters);
      await dispatch('fetchLog', { page: 1 });
    },

    async clearFilters({ commit, dispatch }) {
      commit('resetFilters');
      await dispatch('fetchLog', { page: 1 });
    },

    async fetchTimeline({ commit }, eventId) {
      commit('setTimelineLoading', true);
      commit('setTimeline', null);
      try {
        commit('setTimeline', await apiGetPulseAuditTimeline(eventId));
      } catch (e) {
        commit('setError', 'Could not load the audit trail for that event.');
      } finally {
        commit('setTimelineLoading', false);
      }
    },

    closeTimeline({ commit }) {
      commit('setTimeline', null);
    },
  },
};
